import { useEffect, useState } from "react";
import { REVIEW_SECTION_FIELDS } from "../../../../data/reviewRequestDetails";
import InfoOrangeIcon from "../../../../assets/icons/info_orange.svg";
import CustomInput from "../../../../commonComponents/CustomInput";
import { FLAG_COMMENT_MAX } from "../../../../components/onboardingModal/FlagModal";
import ReviewField, { getFlagKey } from "../ReviewField";
import SectionDocuments from "../SectionDocuments";
import "./EmploymentSection.css";

const UAN_VERIFICATION_FAILED_NOTE =
  "The UAN could not be verified. Please verify it using the EPFO passbook.";

const BLANK_EXPERIENCE_CHIP_LABELS = [
  "Company Name",
  "Designation",
  "Start Date",
  "Last Date",
  "Resignation Letter",
];

export const BLANK_EMPLOYMENT_EXPERIENCE_FLAGS = [
  {
    // Maps to canonical approval_feedback field_name: this_is_my_last_experience
    fieldKey: "is_last_experience",
    recordIndex: null,
    fieldLabel: "This is my last experience",
  },
  {
    fieldKey: "doc-resignation",
    recordIndex: null,
    fieldLabel: "Resignation Letter",
  },
  {
    fieldKey: "company",
    recordIndex: 0,
    fieldLabel: "Company Name",
  },
  {
    fieldKey: "designation",
    recordIndex: 0,
    fieldLabel: "Designation",
  },
  {
    fieldKey: "start_date",
    recordIndex: 0,
    fieldLabel: "Start Date",
  },
  {
    fieldKey: "last_date",
    recordIndex: 0,
    fieldLabel: "Last Date",
  },
];

const isBlankValue = (value) =>
  value == null || String(value).trim() === "";

const hasJobExperienceDetails = (records = []) =>
  records.some(
    (record) =>
      !isBlankValue(record?.company) ||
      !isBlankValue(record?.designation) ||
      !isBlankValue(record?.start_date) ||
      !isBlankValue(record?.last_date),
  );

export const areBlankExperienceFlagsApplied = (fieldFlags = {}) =>
  BLANK_EMPLOYMENT_EXPERIENCE_FLAGS.every((item) => {
    const flagKey = getFlagKey("employment", item.fieldKey, item.recordIndex);
    return !!fieldFlags[flagKey];
  });

export const getBlankExperienceRemark = (fieldFlags = {}) => {
  const first = BLANK_EMPLOYMENT_EXPERIENCE_FLAGS[0];
  if (!first) return "";
  const flagKey = getFlagKey("employment", first.fieldKey, first.recordIndex);
  return String(fieldFlags[flagKey]?.remark || "");
};

/** True when blank-experience flags are on but the shared remark is empty. */
export const hasMissingBlankExperienceRemark = (fieldFlags = {}) => {
  if (!areBlankExperienceFlagsApplied(fieldFlags)) return false;
  return !String(getBlankExperienceRemark(fieldFlags) || "").trim();
};

const getEmploymentExperienceBadge = (record) => {
  if (!record) return "";
  if (record.current_check || record.is_last_experience) {
    return "Last Company";
  }
  return "";
};

const isUanUnverified = (uanVerified) =>
  uanVerified === false ||
  String(uanVerified ?? "").trim().toLowerCase() === "false";

const ANY_REMARK = /^[\s\S]*$/;

const BlankExperienceCard = ({
  candidateName,
  isFlagged,
  disabled,
  remark,
  isRemarkError = false,
  onToggle,
  onRemarkChange,
}) => {
  const displayName = candidateName || "the candidate";
  const blankCount = BLANK_EXPERIENCE_CHIP_LABELS.length;

  return (
    <div className="rr-blank-experience">
      <div className="rr-blank-experience__header">
        <img
          className="rr-blank-experience__icon"
          src={InfoOrangeIcon}
          alt=""
        />
        <div className="rr-blank-experience__copy">
          <div className="rr-blank-experience__title">
            Job Details were left blank
          </div>
          <div className="rr-blank-experience__subtitle">
            {displayName} left {blankCount} items in this section blank — they
            weren&apos;t mandatory at submission, so there&apos;s nothing on
            file.
          </div>
        </div>
      </div>

      <div className="rr-blank-experience__chips">
        {BLANK_EXPERIENCE_CHIP_LABELS.map((label) => (
          <span className="rr-blank-experience__chip" key={label}>
            {label}
          </span>
        ))}
      </div>

      <div className="rr-blank-experience__divider" />

      <div className="rr-blank-experience__request">
        <div className="rr-blank-experience__request-copy">
          <div className="rr-blank-experience__request-title">
            Request all of them from {displayName}
          </div>
          <div className="rr-blank-experience__request-subtitle">
            Each blank item gets flagged and highlighted for them to fill in.
          </div>
        </div>
        <button
          type="button"
          className={`rr-blank-experience__switch${
            isFlagged ? " is-on" : ""
          }${disabled ? " is-disabled" : ""}`}
          role="switch"
          aria-checked={isFlagged}
          aria-disabled={disabled}
          disabled={disabled}
          aria-label={`Request all blank job experience items from ${displayName}`}
          onClick={() => {
            if (disabled) return;
            onToggle?.(!isFlagged);
          }}
        >
          <span className="rr-blank-experience__switch-thumb" aria-hidden="true" />
        </button>
      </div>

      {isFlagged ? (
        <div className="rr-blank-experience__remark">
          <CustomInput
            label="Remark"
            value={remark}
            placeholder="Enter remark"
            editable={true}
            readOnly={false}
            preview={true}
            isRequired={true}
            isError={isRemarkError}
            additionalValText="Enter a remark, or disable the switch"
            validationStr={ANY_REMARK}
            onChange={(value) => {
              const nextValue = String(value || "").slice(0, FLAG_COMMENT_MAX);
              onRemarkChange?.(nextValue);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

const EmploymentSection = ({
  formData,
  fieldFlags,
  flagModeEnabled,
  restrictToUpdatedFields,
  updatedFields,
  flagModal,
  focusedFlagKey,
  onOpenFlagModal,
  onUpdateField,
  documents = [],
  getFileIcon,
  onPreviewDoc,
  candidateName = "",
  readOnly = false,
  onToggleBlankExperienceFlags,
  onUpdateBlankExperienceRemark,
  blankExperienceRemarkError = false,
  onBlankExperienceRemarkErrorChange,
}) => {
  const employment = formData?.employment || {};
  const records = Array.isArray(employment.records) ? employment.records : [];
  const sharedFields = REVIEW_SECTION_FIELDS.employmentShared || [];
  const recordFields = REVIEW_SECTION_FIELDS.employment || [];
  const showUanVerificationNote = isUanUnverified(employment.uan_verified);
  const hasExperience = hasJobExperienceDetails(records);
  const blankExperienceFlagged = areBlankExperienceFlagsApplied(fieldFlags);
  const [blankExperienceRemark, setBlankExperienceRemark] = useState(
    () => getBlankExperienceRemark(fieldFlags),
  );
  const switchDisabled = readOnly || !flagModeEnabled;
  const blankExperienceSwitchOn =
    blankExperienceFlagged && !!flagModeEnabled && !readOnly;

  useEffect(() => {
    const fromFlags = getBlankExperienceRemark(fieldFlags);
    if (fromFlags) setBlankExperienceRemark(fromFlags);
  }, [fieldFlags]);

  const fieldProps = {
    sectionKey: "employment",
    formData,
    fieldFlags,
    flagModeEnabled,
    restrictToUpdatedFields,
    updatedFields,
    flagModal,
    focusedFlagKey,
    onOpenFlagModal,
    onUpdateField,
  };

  let content;
  if (!hasExperience) {
    content = (
      <>
        {employment.uan ? (
          <div className="rr-employment-shared flex flex-wrap">
            {sharedFields.map((field) => (
              <ReviewField
                key={field.key}
                {...fieldProps}
                field={field}
                options={
                  field.key === "uan_verified_at" && showUanVerificationNote
                    ? { fieldNote: UAN_VERIFICATION_FAILED_NOTE }
                    : undefined
                }
              />
            ))}
          </div>
        ) : null}
        <BlankExperienceCard
          candidateName={candidateName}
          isFlagged={blankExperienceSwitchOn}
          disabled={switchDisabled}
          remark={blankExperienceRemark}
          isRemarkError={blankExperienceRemarkError}
          onToggle={(enabled) => {
            if (!enabled) {
              setBlankExperienceRemark("");
              onBlankExperienceRemarkErrorChange?.(false);
            }
            onToggleBlankExperienceFlags?.(
              enabled,
              enabled ? blankExperienceRemark : "",
            );
          }}
          onRemarkChange={(value) => {
            setBlankExperienceRemark(value);
            if (String(value || "").trim()) {
              onBlankExperienceRemarkErrorChange?.(false);
            }
            if (blankExperienceSwitchOn) {
              onUpdateBlankExperienceRemark?.(value);
            }
          }}
        />
      </>
    );
  } else {
    content = (
      <>
        <div className="rr-employment-shared flex flex-wrap">
          {sharedFields.map((field) => (
            <ReviewField
              key={field.key}
              {...fieldProps}
              field={field}
              options={
                field.key === "uan_verified_at" && showUanVerificationNote
                  ? { fieldNote: UAN_VERIFICATION_FAILED_NOTE }
                  : undefined
              }
            />
          ))}
        </div>
        {records.map((record, recordIndex) => {
          const badge = getEmploymentExperienceBadge(record);
          const recordLabel =
            record.company || `Employment ${recordIndex + 1}`;

          return (
            <div
              className="rr-employment-record"
              key={`employment-record-${recordIndex}`}
            >
              {badge ? (
                <div className="rr-employment-record-head">
                  <span className="rr-employment-badge is-last">{badge}</span>
                </div>
              ) : null}
              <div className="flex flex-wrap">
                {recordFields.map((field) => (
                  <ReviewField
                    key={`${recordIndex}-${field.key}`}
                    {...fieldProps}
                    field={field}
                    options={{
                      recordIndex,
                      recordLabel,
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap">{content}</div>
      <SectionDocuments
        sectionKey="employment"
        documents={documents}
        getFileIcon={getFileIcon}
        onPreviewDoc={onPreviewDoc}
        fieldFlags={fieldFlags}
        flagModeEnabled={flagModeEnabled}
        restrictToUpdatedFields={restrictToUpdatedFields}
        updatedFields={updatedFields}
        flagModal={flagModal}
        focusedFlagKey={focusedFlagKey}
        onOpenFlagModal={onOpenFlagModal}
      />
    </>
  );
};

export default EmploymentSection;
